import React, { useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { useNavigate } from 'react-router-dom';
import { AppStrings, logApiErrorToSentry, truncatedText } from '../../utils';
import { remittanceActions } from '../../state/remittanceSlice';
import { PassportIcon } from '../../../../assets/Icons';
import fileImage from "../../../../assets/svg/file.svg";
import Eye from "../../../../assets/svg/Eye.svg";
import UploadCloud from "../../../../assets/svg/UploadCloud.svg";
import Refresh from "../../../../assets/svg/Refresh.svg";
import Typography from '../../../../components/ui/Typography';
import clsx from 'clsx';
import Button from '../../../../components/ui/Button';
import { remittanceApi } from '../../api/remittanceApi';
import { toast } from 'react-toastify';

interface DocumentItemProps {
  item: any;
}

const DocumentItem: React.FC<DocumentItemProps> = ({
   item 
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const remitterState = useAppSelector((state) => state.remittance);
  const { calculator, remitter, recipient, documents } = useAppSelector((state) => state.remittance);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isDragOver, setIsDragOver] = React.useState(false);

  const onClickFileUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleDragOver = (e:any) => {
    e.preventDefault();
    if(item?.loading){
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e:any) => {
    e.preventDefault();
    if(item?.loading){
      setIsDragOver(false);
    }
  };

  const allowedTypes = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/heic",
    "application/pdf"
  ];

  const handleDrop = (e:any) => {
    e.preventDefault();
    setIsDragOver(false);
    if (item?.loading) return;

    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    if (!allowedTypes.includes(file.type)) {
      toast.error("Only JPG, JPEG, PNG, HEIC, or PDF formats are allowed");
      return;
    }

    handleFileUpload(e.dataTransfer.files || null);
  };

  const replaceByDocument = (
    list: any[],
    documentName: string,
    newObject: any
  ) => {
    return list.map((item) =>
      item.document === documentName ? { ...newObject } : item
    );
  };

  const reuploadDocs = () => {
    const requestDocs = { ...item, leadId: null, leadsData: null, docName: "" };
    const requestDocsList = replaceByDocument(
      documents?.recipientLeadDocs,
      item?.document,
      requestDocs
    );
    dispatch(remittanceActions.setRecipientLeadDocsState(requestDocsList));
  }
  
  const normalizeCode = (str: string) => str?.trim().replace(/[\s\-_]+/g, "_").replace(/^_+/, "");

  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFileUpload = async (files: File[] | FileList | null) => {
    const normalizedFiles = Array.isArray(files)
      ? files
      : files
        ? Array.from(files)
        : [];
    const fileData = normalizedFiles[0];
    if (!fileData) {
      resetFileInput();
      return;
    }
    if (!allowedTypes.includes(fileData.type)) {
      toast.error("Only JPG, JPEG, PNG, HEIC, or PDF formats are allowed");
      resetFileInput();
      return;
    }
    const file = new FormData();
    file.append("file", fileData);
    const transactionPurpose = calculator?.amountCalculation?.transaction_purpose?.replace(/\s+/g, "-") || "";
    const docCode = normalizeCode(item?.document).replace(/`+$/, "");
    const formData = { file, remitterId: remitter?.selectedRemitter?.userKey, transactionPurpose, docCode };
    const requestDocs = { ...item, loading: true, error: "" };
    const requestDocsList = replaceByDocument(
      documents?.recipientLeadDocs,
      item?.document,
      requestDocs
    );
    dispatch(remittanceActions.setRecipientLeadDocsState(requestDocsList));
    remittanceApi.uploadRecipientDocs(formData)
      .then((response) => {
        if(!response?.data?.leadDocId) {
          toast.error(AppStrings.GENERIC_ERROR);
        }
        const updatedDocs = { ...item, leadsData: null, docName: fileData.name, leadId: response?.data?.leadDocId, loading: false };
        const updatedDocsList = replaceByDocument(
          documents?.recipientLeadDocs,
          item?.document,
          updatedDocs
        );
        dispatch(remittanceActions.setRecipientLeadDocsState(updatedDocsList));
      })
      .catch((error) => {
        console.error("ERROR", error);
        const errorMsg = error?.response?.data?.error?.type || error?.response?.data?.error || error?.response?.error || AppStrings.GENERIC_ERROR;
        toast.error(errorMsg);
        const requestDocs = { ...item, loading: false, error: errorMsg };
        const requestDocsList = replaceByDocument(
          documents?.recipientLeadDocs,
          item?.document,
          requestDocs
        );
        dispatch(remittanceActions.setRecipientLeadDocsState(requestDocsList));
        logApiErrorToSentry(error, "handleFileUpload.uploadRecipientDocs");
      })
      .finally(() => {
        resetFileInput();
      });
  }

  const handlePreviewDoc = () => {
    if(item?.leadId) {
      dispatch(remittanceActions.setModalType("PREVIEW_DOCS"));
      dispatch(remittanceActions.setRecipientPreviewDocsState({ loading: true }));
      remittanceApi.getRecipientDocsDownload(item?.leadId)
        .then((response: any) => {
          if (response && response.data) {
            const blob = new Blob([response.data], { type: "application/json" });
            const fileURL = URL.createObjectURL(blob);
            dispatch(remittanceActions.setRecipientPreviewDocsState({ loading: false, fileURL: fileURL }));
          } else {
            toast.error(AppStrings.GENERIC_ERROR);
          }
        })
        .catch((error: any) => {
          const errorMsg = error?.response?.data?.error?.type || error?.response?.data?.error || error?.response?.error || AppStrings.GENERIC_ERROR;
          toast.error(errorMsg);
          dispatch(remittanceActions.setRecipientPreviewDocsState({ loading: false, fileURL: "" }));
          logApiErrorToSentry(error, "handlePreviewDoc.getRecipientDocsDownload");
        })

    }
  }

  return(
    <div className="flex flex-col w-full lg:w-auto my-5">

      <div
        className={`hidden w-[190px] lg:flex flex-col items-center justify-center px-1 relative rounded overflow-hidden ${item?.leadId ? "bg-[#2E7D331A] h-[133px]" : "bg-[#F9F9F9] h-[160px]"}`}
        style={{ cursor: "pointer" }}
        onClick={onClickFileUpload}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="flex flex-col items-center">
          <PassportIcon />
          <Typography
            tagType="div"
            className="relative text-xs font-normal text-black-200 mt-[22px] text-center"
          >
            {item?.document}
            {item?.isRequired && (
              <span className="text-lg text-red-500">*</span>
            )}
          </Typography>
        </div>
        <Typography
          tagType="div"
          text="(If available)"
          className={clsx(
            item?.document === "Rent Agreement Invoice" ? "block" : "hidden",
            "text-xs font-normal text-black-200 text-center",
          )}
        />
      </div>

      {item?.leadId && (
        <div className="flex justify-start items-center pt-2">
          <img src={fileImage} />
          <Typography
            tagType="div"
            text={item?.docName !== "" && item?.docName.length > 15 ? truncatedText(item?.docName) : item?.docName
            }
            className="hidden pl-1 text-sm font-semibold text-black-500 lg:flex"
          />
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,application/pdf"
        className="hidden"
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => { 
          handleFileUpload(e.target.files);
        }}
      />

      <Button
        bgColor={item?.leadId ? "bg-[#2E7D33] text-white" : "bg-[#7C8694] text-white"}
        className={
          "relative h-[50px] w-[190px] hidden items-center justify-center lg:flex rounded cursor-pointer mt-2"
        }
        onClickHandler={(event:any) => {
          item?.leadId && !item?.loading ? handlePreviewDoc() : onClickFileUpload();
        }}
        isLoading={item?.loading}
        disabled={item?.loading}
      >
        {item?.leadId ? (
          <div className="flex justify-center items-center w-full">
            <img src={Eye} />
            <p className="pl-2 text-sm font-semibold">View</p>
          </div>
        ) : (
          <div className="flex items-center justify-center">
            <img src={UploadCloud} />
            <span className="pl-2 text-sm font-semibold">Upload</span>
          </div>
        )}
      </Button>

      <div className="flex flex-col items-center">
        {item?.leadId && (
          <div
            className="hidden mt-2 text-base font-semibold text-center cursor-pointer text-[#586372] underline lg:flex"
            onClick={reuploadDocs}
          >
            <img src={Refresh} className="pr-2" />
            <p className="text-sm">Reupload</p>
          </div>
        )}
        {item?.error != "" && (
          <div className="flex justify-center items-center">
            <Typography tagType="div" text={item?.error} className="text-sm font-normal w-[190px] text-wrap text-center text-red-100" />
          </div>
        )}
      </div>

    </div>
  );
};

export default DocumentItem;